import { useQuery } from '@tanstack/react-query';
import { Server } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { Builder, ProjectSettings } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { ACCESS_METHOD_LABEL, useAssistantStatus, type RepositoryAccessCheck } from '../../api/assistant';
import { SettingsNav } from './SettingsNav';

interface Impact {
  activeClaims: string[];
  readyTickets: string[];
  releaseApprovals: string[];
  unresolvedDeployments: number;
}

/** PRJ-07: one canonical repository and multiple named builders with machine-specific paths. */
export function RepositoryBuildersPage() {
  const { key } = useParams();
  const admin = useIsAdmin();
  const project = useProject(key);
  usePageTitle(project.data ? `${project.data.name} · Repository and builders` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Settings', to: `/projects/${key}/settings` },
    { label: 'Repository and builders' },
  ]);
  const q = useQuery({
    queryKey: ['settings', key],
    queryFn: () => api.get<ProjectSettings>(`/projects/${key}/settings`),
    enabled: admin,
  });
  const [editing, setEditing] = useState<Builder | 'new' | null>(null);
  const [health, setHealth] = useState<Builder | null>(null);
  if (!admin) return <PageUnavailable />;
  const archived = !!project.data?.archived;
  return (
    <div className="page">
      <PageHeader
        title="Repository and builders"
        subtitle="Paths are metadata for external tools on the named machine. The application never reads them or runs commands."
      />
      <SettingsNav projectKey={key!} />
      <QueryView query={q}>
        {(s) => (
          <>
            <RepositoryCard projectKey={key!} settings={s} archived={archived} />
            {s.repository && <AssistantAccessCard projectKey={key!} branch={s.repository.defaultBranch} />}
            <Card
              title="Builders"
              subtitle="Named machines where external AI implements work in isolated worktrees."
              action={
                <Button size="sm" onClick={() => setEditing('new')} disabled={archived}>
                  Register builder
                </Button>
              }
            >
              {s.builders.length === 0 ? (
                <EmptyState icon={Server} title="No builders yet">
                  Register at least one builder so AI services can claim work.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Machine</th>
                        <th>Base checkout path</th>
                        <th>Health (supplied)</th>
                        <th className="num">Claims</th>
                        <th>State</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {s.builders.map((b) => (
                        <tr key={b.id}>
                          <td>
                            <div className="primary-cell">{b.name}</div>
                            <div className="small muted">{b.description}</div>
                          </td>
                          <td className="mono">{b.machineId}</td>
                          <td className="mono small" style={{ overflowWrap: 'anywhere' }}>
                            {b.basePath}
                          </td>
                          <td className="small">
                            {b.healthStatus ? (
                              <>
                                <Badge
                                  tone={
                                    b.healthStatus === 'healthy'
                                      ? 'success'
                                      : b.healthStatus === 'degraded'
                                        ? 'warning'
                                        : 'error'
                                  }
                                >
                                  {b.healthStatus}
                                </Badge>
                                <div className="muted">
                                  Reported by {b.healthReportedBy} ·{' '}
                                  <Timestamp value={b.healthObservedAt} relative />
                                </div>
                              </>
                            ) : (
                              <span className="muted">No observation</span>
                            )}
                          </td>
                          <td className="num">{b.activeClaims}</td>
                          <td>{b.active ? <Badge tone="success">Active</Badge> : <Badge>Retired</Badge>}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHealth(b)}
                              disabled={archived}
                            >
                              Record health
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing(b)}
                              disabled={archived}
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {editing && (
              <BuilderDialog
                projectKey={key!}
                builder={editing === 'new' ? null : editing}
                onClose={() => setEditing(null)}
              />
            )}
            {health && <HealthDialog projectKey={key!} builder={health} onClose={() => setHealth(null)} />}
          </>
        )}
      </QueryView>
    </div>
  );
}

function RepositoryCard({
  projectKey,
  settings,
  archived,
}: {
  projectKey: string;
  settings: ProjectSettings;
  archived: boolean;
}) {
  const repo = settings.repository;
  const [editing, setEditing] = useState(!repo);
  const [url, setUrl] = useState(repo?.url ?? '');
  const [branch, setBranch] = useState(repo?.defaultBranch ?? 'main');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const replacing = !!repo && url.trim() !== repo.url;
  const impact = useQuery({
    queryKey: ['repo-impact', projectKey],
    queryFn: () => api.get<Impact>(`/projects/${projectKey}/repository/impact`),
    enabled: confirm,
  });
  const save = useAction(
    () =>
      api.put(`/projects/${projectKey}/repository`, {
        url,
        defaultBranch: branch,
        reason: reason || undefined,
      }),
    {
      success: replacing ? 'Repository replaced' : 'Repository saved',
      invalidate: [
        ['settings', projectKey],
        ['project', projectKey],
      ],
      onSuccess: () => {
        setConfirm(false);
        setEditing(false);
      },
    },
  );
  return (
    <Card
      title="Canonical repository"
      subtitle="One repository per project. Never include credentials in the URL."
      action={
        repo &&
        !editing && (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)} disabled={archived}>
            Change
          </Button>
        )
      }
    >
      {!editing && repo ? (
        <KeyValue
          items={[
            [
              'URL',
              <span key="u" className="mono">
                {repo.url}
              </span>,
            ],
            [
              'Default branch',
              <span key="b" className="mono">
                {repo.defaultBranch}
              </span>,
            ],
            ['Registered', <Timestamp key="t" value={repo.createdAt} />],
          ]}
        />
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (replacing) setConfirm(true);
            else save.mutate(undefined);
          }}
        >
          <div className="grid-2">
            <Field label="Repository URL">
              {(p) => (
                <Input
                  {...p}
                  className="mono"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://git.example.com/org/repo.git"
                  required
                />
              )}
            </Field>
            <Field label="Default branch">
              {(p) => (
                <Input
                  {...p}
                  className="mono"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  required
                />
              )}
            </Field>
          </div>
          {replacing && (
            <>
              <Alert tone="warning">
                Replacing the repository fences active claims for admin recovery, returns ready tickets to
                Todo for rework, and invalidates release approvals.
              </Alert>
              <Field label="Reason">
                {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} required />}
              </Field>
            </>
          )}
          <ErrorAlert error={!confirm ? save.error : null} />
          <div className="row">
            <Button type="submit" loading={save.isPending && !confirm} disabled={archived}>
              {replacing ? 'Review replacement' : 'Save repository'}
            </Button>
            {repo && (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  setUrl(repo.url);
                  setBranch(repo.defaultBranch);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}
      {settings.repositoryHistory.length > 0 && (
        <details>
          <summary className="small muted" style={{ cursor: 'pointer' }}>
            Previous repositories ({settings.repositoryHistory.length})
          </summary>
          <ul className="checklist" style={{ marginTop: 8 }}>
            {settings.repositoryHistory.map((r) => (
              <li key={r.id} className="small">
                <span className="mono">{r.url}</span>{' '}
                <span className="muted">
                  · replaced <Timestamp value={r.replacedAt} dateOnly />
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {confirm && (
        <ConfirmDialog
          title="Replace repository?"
          confirmLabel="Yes, replace"
          variant="danger"
          onClose={() => setConfirm(false)}
          onConfirm={() => save.mutate(undefined)}
          pending={save.isPending}
          error={save.error}
          disabled={!impact.data || impact.data.unresolvedDeployments > 0}
        >
          {impact.data ? (
            <ul className="checklist">
              <li>Active claims fenced: {impact.data.activeClaims.join(', ') || 'none'}</li>
              <li>Ready tickets returned to Todo: {impact.data.readyTickets.join(', ') || 'none'}</li>
              <li>Release approvals invalidated: {impact.data.releaseApprovals.join(', ') || 'none'}</li>
              {impact.data.unresolvedDeployments > 0 && (
                <li className="strong">
                  Refused: {impact.data.unresolvedDeployments} deployment(s) unresolved. Reconcile first.
                </li>
              )}
            </ul>
          ) : (
            <p className="muted">Loading impact…</p>
          )}
        </ConfirmDialog>
      )}
    </Card>
  );
}

function BuilderDialog({
  projectKey,
  builder,
  onClose,
}: {
  projectKey: string;
  builder: Builder | null;
  onClose: () => void;
}) {
  const [v, setV] = useState({
    name: builder?.name ?? '',
    machineId: builder?.machineId ?? '',
    basePath: builder?.basePath ?? '',
    description: builder?.description ?? '',
    active: builder?.active ?? true,
  });
  const save = useAction(
    () =>
      builder
        ? api.patch(`/projects/${projectKey}/builders/${builder.id}`, {
            ...v,
            expectedVersion: builder.version,
          })
        : api.post(`/projects/${projectKey}/builders`, {
            name: v.name,
            machineId: v.machineId,
            basePath: v.basePath,
            description: v.description,
          }),
    {
      success: builder ? 'Builder updated' : 'Builder registered',
      invalidate: [
        ['settings', projectKey],
        ['project', projectKey],
      ],
      onSuccess: onClose,
    },
  );
  return (
    <Modal
      title={builder ? `Edit ${builder.name}` : 'Register builder'}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate(undefined)} loading={save.isPending}>
            {builder ? 'Save changes' : 'Register builder'}
          </Button>
        </>
      }
    >
      <div className="grid-2">
        <Field label="Name">
          {(p) => (
            <Input
              {...p}
              value={v.name}
              onChange={(e) => setV({ ...v, name: e.target.value })}
              placeholder="build-01"
              data-autofocus
            />
          )}
        </Field>
        <Field
          label="Machine identifier"
          hint="Run 'lixionary-jira machine-id' on the builder to get its deterministic identity."
        >
          {(p) => (
            <Input
              {...p}
              className="mono"
              value={v.machineId}
              onChange={(e) => setV({ ...v, machineId: e.target.value })}
              placeholder="mid_... or machine name"
            />
          )}
        </Field>
      </div>
      <Field
        label="Absolute base checkout path"
        hint="Meaningful only on that machine. Agents create isolated worktrees beneath it."
      >
        {(p) => (
          <Input
            {...p}
            className="mono"
            value={v.basePath}
            onChange={(e) => setV({ ...v, basePath: e.target.value })}
            placeholder="/Users/ci/src/atlas"
          />
        )}
      </Field>
      <Field label="Description" optional>
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            value={v.description}
            onChange={(e) => setV({ ...v, description: e.target.value })}
          />
        )}
      </Field>
      {builder && (
        <Checkbox
          label="Active"
          description="Retiring requires recovering this builder's active claims first."
          checked={v.active}
          onChange={(active) => setV({ ...v, active })}
        />
      )}
      <ErrorAlert error={save.error} />
    </Modal>
  );
}

function HealthDialog({
  projectKey,
  builder,
  onClose,
}: {
  projectKey: string;
  builder: Builder;
  onClose: () => void;
}) {
  const [status, setStatus] = useState('healthy');
  const [note, setNote] = useState('');
  const save = useAction(
    () => api.post(`/projects/${projectKey}/builders/${builder.id}/health`, { status, note }),
    { success: 'Observation recorded', invalidate: [['settings', projectKey]], onSuccess: onClose },
  );
  return (
    <Modal
      title={`Record health for ${builder.name}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate(undefined)} loading={save.isPending}>
            Record observation
          </Button>
        </>
      }
    >
      <p className="muted">This records what you observed. The application does not check the machine.</p>
      <Field label="Status">
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="healthy">Healthy</option>
            <option value="degraded">Degraded</option>
            <option value="unreachable">Unreachable</option>
          </Select>
        )}
      </Field>
      <Field label="Note" optional>
        {(p) => <Input {...p} value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      <ErrorAlert error={save.error} />
    </Modal>
  );
}

/** Whether the ticket assistant can read the configured branch, with the credentials it would use. */
function AssistantAccessCard({ projectKey, branch }: { projectKey: string; branch: string }) {
  const status = useAssistantStatus();
  const check = useAction(() =>
    api.post<RepositoryAccessCheck>(`/projects/${projectKey}/repository/assistant-access`),
  );
  const r = check.data;
  if (!status.data?.available) return null;
  return (
    <Card
      title="Ticket assistant access"
      subtitle={`The assistant plans tickets from the ${branch} branch. It only reads; it never pushes.`}
      action={
        <Button
          size="sm"
          variant="secondary"
          loading={check.isPending}
          onClick={() => check.mutate(undefined)}
        >
          Check access
        </Button>
      }
    >
      <ErrorAlert error={check.error} />
      {r?.ok && (
        <Alert tone="success" title={`Can read ${r.branch}`}>
          <span>
            Latest commit <span className="mono">{r.revision.slice(0, 12)}</span> via{' '}
            {ACCESS_METHOD_LABEL[r.via]}.
          </span>
        </Alert>
      )}
      {r && !r.ok && (
        <Alert tone="error" title="The assistant cannot read this repository">
          <span>{r.detail}</span>
          {r.installUrl && (
            <a href={r.installUrl} target="_blank" rel="noreferrer">
              Install the GitHub App on this repository
            </a>
          )}
        </Alert>
      )}
      {!r && !check.error && (
        <span className="small muted">
          Checks the credentials the assistant would use and that the branch exists, without cloning.
        </span>
      )}
    </Card>
  );
}
